<template>
  <div>
    <div v-if="loading">Loading...</div>
    <div v-else class="pb-4">
      <v-tabs v-model="tab">
        <v-tab>{{ $t("projects.slideManagement.add.details.image.title") }}</v-tab>
        <v-tab>{{ $t("projects.slideManagement.add.details.pathologist.title") }}</v-tab>
        <v-tab>{{ $t("projects.slideManagement.add.details.sample.title") }}</v-tab>
        <v-tab>{{ $t("projects.slideManagement.add.details.preview.title") }}</v-tab>
      </v-tabs>
      <v-tabs-items :value="tab" class="py-3">
        <v-tab-item>
          <div class="v-data-table theme--light">
            <div class="v-data-table__wrapper">
              <table>
                <tbody>
                  <tr>
                    <td><strong>
                      {{ $t("projects.slideManagement.add.details.image.pixelDepth") }}
                    </strong></td>
                    <td>
                      {{
                        $dp.getFromExtension(
                          item,
                          "http://deeppath.cl/fhir/StructureDefinition/PixelDepthExt",
                          "valueCode"
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>
                      {{ $t("projects.slideManagement.add.details.image.pixelSizeX") }}
                    </strong></td>
                    <td>
                      <!--                  Vue does not allow temporary variables in templates, so two method calls necessary here. it's ugly though -->
                      {{
                        $dp.getFromComplexExtension(
                          item,
                          "http://deeppath.cl/fhir/StructureDefinition/pixel-size"
                        ).x
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>
                      {{ $t("projects.slideManagement.add.details.image.pixelSizeY") }}
                    </strong></td>
                    <td>
                      <!--                  Vue does not allow temporary variables in templates, so two method calls necessary here. it's ugly though -->
                      {{
                        $dp.getFromComplexExtension(
                          item,
                          "http://deeppath.cl/fhir/StructureDefinition/pixel-size"
                        ).y
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>
                    {{ $t("projects.slideManagement.add.details.image.table.magnification") }}
                  </th>
                  <th>
                    {{ $t("projects.slideManagement.add.details.image.table.dimensions") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="m of $dp.getFromComplexExtension(
                    item,
                    'http://deeppath.cl/fhir/StructureDefinition/MagnificationExt'
                  )"
                  :key="m.magnification"
                >
                  <td>{{ m.magnification }}</td>
                  <td>{{ m["resolution-x"] }} x {{ m["resolution-y"] }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-tab-item>
        <v-tab-item>
          <div>
            {{ $t("projects.slideManagement.add.details.pathologist.diagnose") }}:
            {{
              $dp.getFromExtension(
                item,
                "http://deeppath.cl/fhir/StructureDefinition/DiagnoseExt",
                "valueCoding"
              ).system
            }}
          </div>
        </v-tab-item>
        <v-tab-item>
          <div>
            {{ $t("projects.slideManagement.add.details.sample.bodySite") }}:
            {{
              specimen(item.specimenResource.reference).collection.bodySite
                .coding[0].code
            }}
          </div>
          <div>
            {{ $t("projects.slideManagement.add.details.sample.tissueType") }}:
            {{ specimen(item.specimenResource.reference).type.coding[0].code }}
          </div>
          <div>
            Procedure:
            {{ $t("projects.slideManagement.add.details.sample.bodySite") }}:
            {{
              specimen(item.specimenResource.reference).processing[0].procedure
                .coding[0].code
            }}
          </div>
          <div>
            {{ $t("projects.slideManagement.add.details.sample.staining") }}:
            {{
              specimen(item.specimenResource.reference).processing[0]
                .description
            }}
          </div>
          <div>
            {{ $t("projects.slideManagement.add.details.sample.collectedOn") }}:
            {{
              specimen(item.specimenResource.reference).collection
                .collectedDateTime
            }}
          </div>
          <div>
            {{ $t("projects.slideManagement.add.details.sample.collectionMethod") }}:
            {{
              specimen(item.specimenResource.reference).collection.method
                .coding[0].code
            }}
          </div>
          <div>
            {{ $t("projects.slideManagement.add.details.sample.collectedFrom") }}:
            {{
              specimen(item.specimenResource.reference).collection.bodySite
                .coding[0].code
            }}
          </div>
        </v-tab-item>
        <v-tab-item>
          <visualization
            :imaging-study="item"
            class="visualization"
            :height="500"
          ></visualization>
        </v-tab-item>
      </v-tabs-items>

      <v-btn
        primary
        @click="onClickAddToProject(item)"
        :disabled="item.inProject"
        :loading="loading"
      >
        <v-icon v-if="!item.inProject">mdi-plus</v-icon>
        {{
          !item.inProject
            ? $t("projects.slideManagement.add.details.add")
            : $t("projects.slideManagement.add.details.added")
        }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { ADD_SLIDE_TO_PROJECT } from "@/store/modules/slides";
import Visualization from "@/components/slide/Visualization";
import { mapGetters } from "vuex";
import { FIND, LOAD } from "@/store/modules/fhir";

export default {
  props: {
    item: Object
  },
  data() {
    return {
      tab: 0,
      loading: false
    };
  },
  computed: {
    ...mapGetters({ specimen: FIND })
  },
  watch: {
    loading(val) {
      this.$emit("loading:changed", val);
    },
    item: {
      immediate: true,
      async handler(val) {
        this.loading = true;
        let res = await this.$store.dispatch(LOAD, val.specimenResource);
        this.loading = false;
      }
    }
  },
  methods: {
    async onClickAddToProject(slide) {
      this.loading = true;
      await this.$store.dispatch(ADD_SLIDE_TO_PROJECT, slide);
      slide.inProject = true;
      // add response to slide.wsi
      this.$emit("added", slide);
      this.$notify({
        text: "Action completed.",
        type: "success"
      });
      this.loading = false;
    }
  },
  components: {
    Visualization
  }
};
</script>

<style scoped></style>
